import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { sizer } from "layout/styles/styled/sizer";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Link } from "react-router-dom";
import { useGetHstImportBatchQuery } from "store/queries/integration";
import styled from "styled-components";
import {
  IHstImportBatchDetail,
  IHstImportBatchRow,
} from "types/HstImportBatch";

import {
  deletedCountLabel,
  formatDateOfService,
  formatDateTime,
  orderPath,
  userDisplayName,
} from "../../utils/hstImports";
import { HstImportFileLinks } from "./HstImportFileLinks";

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(2)};
  margin-bottom: ${sizer(4)};

  h4 {
    margin: 0;
  }
`;

const CountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: ${sizer(2)};
`;

const CountCard = styled.div`
  padding: ${sizer(2)};
  background-color: var(--surface-ground);
  border-radius: 4px;
  text-align: center;

  .count {
    font-size: 20px;
    font-weight: bold;
  }

  .label {
    font-size: 12px;
    color: var(--text-color-secondary);
  }
`;

interface IHstImportDetailDialogProps {
  batchId: string | null;
  onHide: () => void;
}

function renderSummary(batch: IHstImportBatchDetail) {
  const counts: Array<[string, number]> = [
    ["Rows", batch.totalRows],
    ["Created", batch.createdCount],
    ["Updated", batch.updatedCount],
    ["Errors", batch.errorCount],
    ["Duplicates (existing)", batch.duplicateCount],
    ["Duplicates (in file)", batch.csvDuplicateCount],
    ["Skipped payer", batch.skippedPayerCount],
    ["Unmapped payer", batch.unmappedPayerCount],
    ["No insurance match", batch.noInsuranceMatchCount],
  ];
  return (
    <CountGrid>
      {counts.map(([label, count]) => (
        <CountCard key={label}>
          <div className="count">{count}</div>
          <div className="label">{label}</div>
        </CountCard>
      ))}
    </CountGrid>
  );
}

function renderUndoSummary(batch: IHstImportBatchDetail) {
  const summary = batch.undoSummary;
  if (!summary) return null;
  const deleted = Object.entries(summary.deletedCounts || {}).filter(
    ([, count]) => count > 0
  );
  return (
    <Section data-testid="hst-import-undo-summary">
      <h4>
        Undone {formatDateTime(batch.undoneAt)}
        {batch.undoneBy ? ` by ${userDisplayName(batch.undoneBy)}` : ""}
      </h4>
      {deleted.length > 0 ? (
        <CountGrid>
          {deleted.map(([key, count]) => (
            <CountCard key={key}>
              <div className="count">{count}</div>
              <div className="label">{deletedCountLabel(key)} deleted</div>
            </CountCard>
          ))}
        </CountGrid>
      ) : (
        <span>Nothing was deleted.</span>
      )}
      {summary.updatedOrdersLeftInPlace.length > 0 && (
        <div>
          {summary.updatedOrdersLeftInPlace.length} updated estimate
          {summary.updatedOrdersLeftInPlace.length === 1
            ? " was"
            : "s were"}{" "}
          left in place: {summary.updatedOrdersLeftInPlace.join(", ")}
        </div>
      )}
    </Section>
  );
}

export function HstImportDetailDialog({
  batchId,
  onHide,
}: IHstImportDetailDialogProps) {
  const { data, isFetching, isError } = useGetHstImportBatchQuery(
    batchId || "",
    { skip: !batchId }
  );
  const batch = data?.data;

  const externalIdBody = (row: IHstImportBatchRow) =>
    row.orderId && row.orderStatus && batch ? (
      <Link
        to={orderPath(row.orderId, row.orderStatus, batch.accountId)}
        onClick={onHide}
      >
        {row.externalId}
      </Link>
    ) : (
      row.externalId
    );

  const actionBody = (row: IHstImportBatchRow) => (
    <Tag
      value={row.action}
      severity={row.action === "Created" ? "success" : "info"}
    />
  );

  const statusBody = (row: IHstImportBatchRow) =>
    row.orderStatus || <span className="text-500 font-italic">deleted</span>;

  const dosBody = (row: IHstImportBatchRow) =>
    formatDateOfService(row.dateOfService);

  const renderBody = () => {
    if (isFetching && !batch) {
      return (
        <div className="flex justify-content-center p-5">
          <ProgressSpinner style={{ width: "40px", height: "40px" }} />
        </div>
      );
    }
    if (isError || !batch) {
      return <div>Could not load this import.</div>;
    }
    return (
      <>
        <Section>
          <div>
            Imported into <strong>{batch.accountName}</strong> on{" "}
            {formatDateTime(batch.createdAt)}
            {batch.importedBy ? ` by ${userDisplayName(batch.importedBy)}` : ""}
          </div>
          {renderSummary(batch)}
        </Section>
        <Section>
          <h4>Files</h4>
          <HstImportFileLinks batch={batch} showFileNames />
        </Section>
        {renderUndoSummary(batch)}
        <Section>
          <h4>Estimates ({batch.rows.length})</h4>
          <PageDataTable
            value={batch.rows}
            dataKey="id"
            paginator={batch.rows.length > 25}
            rows={25}
            emptyMessage="This import did not create or update any estimates."
          >
            <Column header="External ID" body={externalIdBody} />
            <Column header="Action" body={actionBody} />
            <Column header="Estimate status" body={statusBody} />
            <Column header="Date of service" body={dosBody} />
          </PageDataTable>
        </Section>
      </>
    );
  };

  return (
    <Dialog
      header="HST import details"
      visible={Boolean(batchId)}
      onHide={onHide}
      style={{ width: "900px", maxWidth: "95vw" }}
    >
      {renderBody()}
    </Dialog>
  );
}
